import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {useAppPasswordCreateMutation} from '#/state/queries/app-passwords'
import {CopyButton} from '#/screens/Settings/components/CopyButton'
import {atoms as a, useTheme} from '#/alf'
import {Admonition} from '#/components/Admonition'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Key_Stroke2_Corner2_Rounded as KeyIcon} from '#/components/icons/Key'
import {SquareBehindSquare4_Stroke2_Corner0_Rounded as CopyIcon} from '#/components/icons/SquareBehindSquare4'
import {Text} from '#/components/Typography'

/**
 * Cooey-specific: mint and show an app password so the user can sign in to other
 * Bluesky apps (bsky.app and other clients) with their handle. Their managed
 * Cooey login is never exposed, so an app password is the way to use apps that
 * do not support Cooey's one-click / SSO sign-in.
 */
export function CooeyAppPasswordDialog({
  control,
}: {
  control: Dialog.DialogControlProps
}) {
  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Inner />
    </Dialog.Outer>
  )
}

function Inner() {
  const {t: l} = useLingui()
  const t = useTheme()
  const {mutate, data, isPending, isError} = useAppPasswordCreateMutation()

  const generate = () => {
    // A unique, schema-valid name (letters, numbers, spaces, dashes, underscores).
    const suffix = Math.random().toString(36).slice(2, 8)
    mutate({name: `Bluesky app ${suffix}`, privileged: false})
  }

  return (
    <Dialog.ScrollableInner label={l`App password for other Bluesky apps`}>
      <View style={[a.gap_lg, a.pt_sm]}>
        {!data ? (
          <>
            <Text style={[a.text_2xl, a.font_semi_bold]}>
              <Trans>Log in to other Bluesky apps</Trans>
            </Text>
            <Text style={[a.text_md, a.leading_snug]}>
              <Trans>
                Other Bluesky apps such as bsky.app sign in with an app
                password, not your Cooey login. Generate one here, then use it
                together with your handle to sign in.
              </Trans>
            </Text>
            <Button
              label={l`Generate app password`}
              size="large"
              variant="solid"
              color="primary"
              onPress={generate}
              disabled={isPending}>
              <ButtonIcon icon={KeyIcon} />
              <ButtonText>
                <Trans>Generate app password</Trans>
              </ButtonText>
            </Button>
            {isError && (
              <Admonition type="error">
                <Trans>
                  Couldn't create an app password. Please try again.
                </Trans>
              </Admonition>
            )}
          </>
        ) : (
          <>
            <Text style={[a.text_2xl, a.font_semi_bold]}>
              <Trans>Here is your app password</Trans>
            </Text>
            <Text style={[a.text_md, a.leading_snug]}>
              <Trans>
                In the other app, sign in with your handle and this password.
              </Trans>
            </Text>
            <CopyButton
              value={data.password}
              label={l`Copy app password`}
              size="large"
              color="secondary">
              <ButtonText>{data.password}</ButtonText>
              <ButtonIcon icon={CopyIcon} />
            </CopyButton>
            <Text
              style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
              <Trans>
                For security, you won't be able to see this again. You can
                revoke app passwords under Privacy and security.
              </Trans>
            </Text>
          </>
        )}
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}
